import { EVENT_SIMULATION_NOTE, type InboxItemDetail } from '@fos/shared/contracts/inbox';
import { MAIL_IMPORTANCE_LABELS, MAIL_RECLASSIFY_LABELS, SEVERITY_LABELS, SEVERITY_TONES } from '@fos/shared/labels';
import { ArrowLeft, Check, Inbox, MoreHorizontal, RotateCcw } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import { appErrorCode } from '../../../lib/api';
import { trpc } from '../../../lib/trpc';
import { ContextBanner } from '../../../ui/Blocks';
import { Button, IconButton } from '../../../ui/Button';
import { Menu, MenuContent, MenuItem, MenuTrigger } from '../../../ui/Menu';
import { Section } from '../../../ui/Section';
import { EmptyState, ErrorState, Skeleton } from '../../../ui/States';
import { Status } from '../../../ui/Status';
import { StatusPill } from '../../../ui/StatusPill';
import { toast } from '../../../ui/Toast';
import { TOUCH_BUTTON } from '../../mobile';
import { useInboxActions, useInboxRefresh } from '../hooks';
import { detailTime, isLessImportantMail, senderLine, snoozedUntilLabel } from '../inbox-format';
import { getInboxMessageView } from '../slots';
import { ReclassifyDialog } from './ReclassifyDialog';
import { RestoreRemovedMeeting, removedMeetingId } from './RestoreRemovedMeeting';
import { SnoozeMenu } from './SnoozeControls';

function DetailSkeleton() {
  return (
    <div role="status" aria-busy="true" aria-label="Wczytywanie wiadomości" className="flex flex-col gap-3">
      <Skeleton className="h-8 w-2/3" />
      <Skeleton className="h-4 w-1/3" />
      <Skeleton className="mt-6 h-4 w-full" />
      <Skeleton className="h-4 w-11/12" />
      <Skeleton className="h-4 w-3/4" />
    </div>
  );
}

/** Ważność obok nadawcy: tylko gdy niesie znaczenie (Pilne, Akceptacja, Do wiadomości; mail — „Mniej ważne”). */
function SeverityStatus({ item }: { item: InboxItemDetail }) {
  const s = item.event.severity;
  if (s === 'normal') return null;
  if (isLessImportantMail(item.event)) return <StatusPill tone="neutral">{MAIL_IMPORTANCE_LABELS.less}</StatusPill>;
  return <StatusPill tone={SEVERITY_TONES[s]}>{SEVERITY_LABELS[s]}</StatusPill>;
}

/**
 * „Oznacz jako mniej ważne / istotne” (17.10.2026) — szybka zmiana ważności odebranego maila (owner / admin / manager,
 * jak „Zmień ważność”): Istotne = Zwykłe, Mniej ważne = Do wiadomości. Powrót do klasyfikacji źródła zdejmuje poprawkę.
 */
function useToggleMailImportance(event: InboxItemDetail['event']) {
  const refresh = useInboxRefresh();
  const mutation = trpc.inbox.reclassify.useMutation();
  const next = event.severity === 'fyi' ? ('normal' as const) : ('fyi' as const);
  const run = () =>
    mutation.mutate(
      {
        eventId: event.id,
        severity: next === event.originalSeverity ? null : next,
        ...(next === event.originalSeverity
          ? {}
          : { reason: next === 'fyi' ? 'Oznaczone ręcznie jako mniej ważne' : 'Oznaczone ręcznie jako istotne' }),
      },
      {
        onSuccess: async () => {
          await refresh();
          toast.success(next === 'fyi' ? 'Oznaczono jako mniej ważne' : 'Oznaczono jako istotne');
        },
        onError: () => toast.error('Nie udało się zmienić ważności.'),
      },
    );
  return { label: `Oznacz jako ${MAIL_RECLASSIFY_LABELS[next].toLowerCase()}`, run };
}

/**
 * Podgląd pozycji Inboxu (Figma 02 „Detail”, kolumna 640 px): tytuł z menu „…”, nadawca i czas, oznaczenie
 * symulacji, treść, powód klasyfikacji (i poprawka właściciela), powiązania; akcje: „Przejdź do akceptacji” albo do
 * rekordu (Primary), Zrobione / Przywróć, Odłóż; w menu: nieprzeczytane / przeczytane, Zmień ważność.
 * Otwarcie nieprzeczytanej oznacza ją jako przeczytaną. Wiadomość z wątkiem poczty (Fala 4) pokazuje widok z kompozycji
 * (`setInboxMessageView`: wątek z „Odpowiedz”, Copilotem, przypisaniem do klienta) z tymi samymi akcjami pozycji.
 */
export function InboxDetail({ id, backTo = '/inbox' }: { id: string; backTo?: string }) {
  const query = trpc.inbox.get.useQuery({ id }, { retry: (n, err) => appErrorCode(err) !== 'NOT_FOUND' && n < 2 });
  const actions = useInboxActions();
  const [reclassifyOpen, setReclassifyOpen] = useState(false);
  const autoRead = useRef<string | null>(null);
  const item = query.data;
  const toggleImportance = useToggleMailImportance(
    item?.event ?? ({ id: '', severity: 'normal', originalSeverity: 'normal' } as InboxItemDetail['event']),
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: jednorazowo na otwartą pozycję
  useEffect(() => {
    if (item?.status !== 'unread' || autoRead.current === item.id) return;
    autoRead.current = item.id;
    actions.markRead.mutate({ ids: [item.id] });
  }, [item?.id, item?.status]);

  const back = (
    <Link
      to={backTo}
      className="mb-1 inline-flex min-h-11 items-center gap-1.5 rounded-xs text-body-s text-fg-secondary hover:text-fg focus-visible:outline-2 focus-visible:outline-accent md:hidden"
    >
      <ArrowLeft size={16} strokeWidth={1.75} aria-hidden />
      Inbox
    </Link>
  );

  if (query.isPending) {
    return (
      <>
        {back}
        <Section>
          <DetailSkeleton />
        </Section>
      </>
    );
  }
  if (query.isError) {
    return (
      <>
        {back}
        <Section>
          {appErrorCode(query.error) === 'NOT_FOUND' ? (
            <EmptyState
              icon={Inbox}
              title="Nie ma tej wiadomości"
              description="Mogła zostać usunięta z Twojego zakresu albo link jest nieaktualny."
            />
          ) : (
            <ErrorState onRetry={() => void query.refetch()} retrying={query.isFetching} />
          )}
        </Section>
      </>
    );
  }

  const data = query.data;
  const e = data.event;
  const now = new Date();
  const done = data.status === 'done';
  const snoozed = data.snoozedUntil && data.snoozedUntil.getTime() > now.getTime() ? data.snoozedUntil : null;

  const markDone = () =>
    actions.markDone.mutate(
      { ids: [data.id] },
      {
        onSuccess: () =>
          toast.success('Oznaczono jako zrobione', {
            action: { label: 'Cofnij', onClick: () => actions.restore.mutate({ ids: [data.id] }) },
          }),
      },
    );

  const menu = (
    <Menu>
      <MenuTrigger asChild>
        <IconButton icon={MoreHorizontal} size={32} label="Więcej akcji" className="max-md:size-11" />
      </MenuTrigger>
      <MenuContent align="end">
        {data.status === 'unread' ? (
          <MenuItem onSelect={() => actions.markRead.mutate({ ids: [data.id] })}>Oznacz jako przeczytane</MenuItem>
        ) : (
          <MenuItem onSelect={() => actions.markUnread.mutate({ ids: [data.id] })}>Oznacz jako nieprzeczytane</MenuItem>
        )}
        {data.canReclassify && e.type === 'message.received' && e.severity !== 'critical' && (
          <MenuItem onSelect={toggleImportance.run}>{toggleImportance.label}</MenuItem>
        )}
        {data.canReclassify && <MenuItem onSelect={() => setReclassifyOpen(true)}>Zmień ważność…</MenuItem>}
      </MenuContent>
    </Menu>
  );
  const itemActions = (
    <>
      {done ? (
        <Button
          variant="secondary"
          icon={RotateCcw}
          className="max-md:h-11"
          loading={actions.restore.isPending}
          onClick={() => actions.restore.mutate({ ids: [data.id] })}
        >
          Przywróć
        </Button>
      ) : (
        <Button
          variant="secondary"
          icon={Check}
          className="max-md:h-11"
          loading={actions.markDone.isPending}
          onClick={markDone}
        >
          Zrobione
        </Button>
      )}
      {!done && <SnoozeMenu itemId={data.id} />}
    </>
  );
  const snoozedBanner = snoozed ? (
    <ContextBanner tone="info">
      <span className="flex flex-wrap items-center justify-between gap-2">
        <span>Odłożone {snoozedUntilLabel(snoozed, now)}.</span>
        <Button
          variant="ghost"
          size="s"
          className={TOUCH_BUTTON}
          onClick={() => actions.snooze.mutate({ id: data.id, until: null })}
        >
          Przywróć teraz
        </Button>
      </span>
    </ContextBanner>
  ) : null;
  const reclassify = data.canReclassify ? (
    <ReclassifyDialog open={reclassifyOpen} onOpenChange={setReclassifyOpen} event={e} />
  ) : null;

  // spotkanie usunięte w Kalendarzu Google: „Przywróć spotkanie” jako główna akcja (przejście do spotkania obok)
  const removedMeeting = removedMeetingId(e);

  const MessageView = e.kind === 'message' && e.threadId ? getInboxMessageView() : null;
  if (MessageView) {
    return (
      <div>
        {back}
        <Section>
          <MessageView item={data} menu={menu} actions={itemActions} banners={snoozedBanner} />
        </Section>
        {reclassify}
      </div>
    );
  }

  return (
    <article aria-labelledby="inbox-detail-title">
      {back}
      {/* podgląd pozycji = jeden biały blok czytania (DECISIONS 2026-10-07 „Nowy układ wizualny”) */}
      <Section>
        <div className="flex items-start gap-2">
          <h2 id="inbox-detail-title" className="min-w-0 flex-1 text-h3 break-words text-fg">
            {e.title}
          </h2>
          {menu}
        </div>

        <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-body-s">
          <span>
            <span className="font-medium text-fg-secondary">{senderLine(e)}</span>
            <span className="text-fg-muted"> · {detailTime(e.occurredAt, now)}</span>
          </span>
          <SeverityStatus item={data} />
          {e.simulated && <Status tone="warning">Symulacja</Status>}
          {done && <StatusPill tone="success">Zrobione</StatusPill>}
        </div>

        {e.simulated && (
          <ContextBanner tone="info" className="mt-5">
            {EVENT_SIMULATION_NOTE}.
          </ContextBanner>
        )}
        {snoozedBanner && <div className="mt-5">{snoozedBanner}</div>}

        <div className="mt-7 text-body-m leading-[1.65] break-words whitespace-pre-wrap text-fg">
          {e.body?.trim() ? (
            e.body
          ) : (
            <span className="text-fg-muted">
              {data.approvalId ? 'Pełna treść akcji i decyzja są w Akceptacjach.' : 'Bez treści.'}
            </span>
          )}
        </div>

        {(e.reason || e.override) && (
          <div className="mt-6 flex flex-col gap-1 text-caption text-fg-muted">
            {e.override ? (
              <p>
                Ważność zmieniona z „{SEVERITY_LABELS[e.originalSeverity]}” na „{SEVERITY_LABELS[e.severity]}”
                {e.override.by ? ` · ${e.override.by.name}` : ''}
                {data.overrideReason ? ` · ${data.overrideReason}` : ''}
              </p>
            ) : null}
            {e.reason && !e.simulated && <p>Powód klasyfikacji: {e.reason}</p>}
          </div>
        )}

        {data.links.length > 0 && (
          <section aria-label="Powiązania" className="mt-6">
            <h3 className="text-caption font-medium text-fg-muted">Powiązania</h3>
            <ul className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 text-body-s max-md:mt-0 max-md:flex-col max-md:gap-y-0">
              {data.links.map((l) => (
                <li key={`${l.type}:${l.id}`}>
                  {l.path ? (
                    <Link
                      to={l.path}
                      className="rounded-xs text-fg-secondary underline-offset-2 hover:text-fg hover:underline focus-visible:outline-2 focus-visible:outline-accent max-md:flex max-md:min-h-11 max-md:items-center"
                    >
                      {l.label}
                    </Link>
                  ) : (
                    <span className="text-fg-secondary">{l.label}</span>
                  )}
                </li>
              ))}
            </ul>
          </section>
        )}

        <div className="mt-8 flex flex-wrap items-center gap-2">
          {removedMeeting && !done && (
            <RestoreRemovedMeeting
              meetingId={removedMeeting}
              onRestored={() => actions.markDone.mutate({ ids: [data.id] })}
            />
          )}
          {data.goTo && (
            <Button asChild variant={removedMeeting ? 'secondary' : 'primary'} className="max-md:h-11">
              <Link to={data.goTo.path}>{data.goTo.label}</Link>
            </Button>
          )}
          {itemActions}
        </div>
      </Section>

      {reclassify}
    </article>
  );
}
